import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { Layout } from '../components/layout';
import { EmptyState, ImageWithLoader } from '../components/ui';
import { SharingTile } from '../components/sharing/SharingTile';
import { SharingCatalogBar } from '../components/sharing/SharingCatalogBar';
import { api } from '../services/api';
import { useUIStore } from '../store';
import { useProductSearch } from '../hooks/useProductSearch';
import { optimizeImage } from '../utils/image';
import { Collection, Product } from '../types';
import {
  DEFAULT_SHARING_FILTERS,
  SharingCategory,
  SharingFilters,
  applySharingFilters,
  cheapestOffer,
  sharingFiltersCount,
  sharingPriceCeiling,
  sharingTypeOf,
} from '../utils/sharing';

type Banner = { id: string; title: string; imageUrl: string; linkUrl: string | null; linkType: string; position: number };

const TITLE = { fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '19px', lineHeight: '24px', letterSpacing: '-0.3px', color: '#1A1D1A' } as const;
/** Секция главной — белая карточка на сером фоне, как подборки на главной каталога */
const CARD = 'bg-white py-5 my-5 first:mt-0 last:mb-0 rounded-2xl';

const PAGE_TITLE = { fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '22px', lineHeight: '28px', letterSpacing: '-0.4px', color: '#1A1D1A' } as const;
const RESULTS_TITLE = { fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '120%', letterSpacing: '-0.3px', color: '#1A1D1A' } as const;

const byName = (a: Product, b: Product) => a.name.localeCompare(b.name, 'ru');

// Последние данные раздела: при возврате из карточки главная шеринга и выдача поиска
// показываются сразу, без скелетона; свежие данные подтягиваются в фоне
let sharingHomeCache: { products: Product[]; collections: Collection[]; banners: Banner[] } | null = null;

/** Выдача поиска и фильтров; при запросе — секциями «Игры» и «Подписки», как поиск в каталоге */
const SharingResults: React.FC<{ items: Product[]; grouped: boolean }> = ({ items, grouped }) => {
  if (!grouped) {
    return (
      <div className="grid grid-cols-2 gap-x-3 gap-y-5">
        {items.map((p) => <SharingTile key={p.id} product={p} />)}
      </div>
    );
  }
  const games = items.filter((p) => sharingTypeOf(p) !== 'subscription');
  const subscriptions = items.filter((p) => sharingTypeOf(p) === 'subscription');
  return (
    <>
      {games.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3" style={RESULTS_TITLE}>Игры</h2>
          <div className="grid grid-cols-2 gap-x-3 gap-y-5">
            {games.map((p) => <SharingTile key={p.id} product={p} />)}
          </div>
        </section>
      )}
      {subscriptions.length > 0 && (
        <section>
          <h2 className="mb-3" style={RESULTS_TITLE}>Подписки</h2>
          <div className="grid grid-cols-2 gap-x-3 gap-y-5">
            {subscriptions.map((p) => <SharingTile key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </>
  );
};

/** Сетка-заглушка выдачи — как в каталоге */
const ResultsSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 gap-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="aspect-square rounded-[14px] bg-gray-200 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    ))}
  </div>
);

/**
 * Главная раздела «Шеринг»: сверху панель как в каталоге (поиск, раздел, фильтры),
 * ниже баннеры и подборки раздела, затем подписки (игры — через подборки и поиск). При поиске или фильтрах
 * вместо них — выдача «Найдено: N». Региона и курса тут нет — цены позиций в рублях как есть.
 */
export const SharingHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>(sharingHomeCache?.products || []);
  const [collections, setCollections] = useState<Collection[]>(sharingHomeCache?.collections || []);
  const [banners, setBanners] = useState<Banner[]>(sharingHomeCache?.banners || []);
  const [loading, setLoading] = useState(!sharingHomeCache);

  const searchQuery = useUIStore((s) => s.sharingSearchQuery);
  const setSearchQuery = useUIStore((s) => s.setSharingSearchQuery);
  const category = useUIStore((s) => s.sharingCategory);
  const setCategory = useUIStore((s) => s.setSharingCategory);
  const filters = useUIStore((s) => s.sharingFilters);
  const setFilters = useUIStore((s) => s.setSharingFilters);
  const setSearchActive = useUIStore((s) => s.setSearchActive);
  const [searchFocused, setSearchFocused] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Пока открыт поиск, нижнее меню прячется — как в каталоге
  useEffect(() => {
    setSearchActive(searchFocused);
    return () => setSearchActive(false);
  }, [searchFocused, setSearchActive]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getProducts({ category: 'sharing', limit: 200 }).catch(() => ({ products: [], total: 0 })),
      api.getCollections('sharing').catch(() => []),
      api.getBanners('sharing').catch(() => []),
    ]).then(([p, c, b]) => {
      if (cancelled) return;
      const live = p.products.filter((x) => cheapestOffer(x));
      sharingHomeCache = { products: live, collections: c, banners: b };
      setProducts(live);
      setCollections(c);
      setBanners(b);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleQueryChange = useCallback((q: string) => setSearchQuery(q), [setSearchQuery]);
  const handleSearchFocus = useCallback((focused: boolean) => setSearchFocused(focused), []);

  const searchResults = useProductSearch(products, searchQuery);
  const hasQuery = searchQuery.trim() !== '';
  const isCatalogMode = hasQuery || category !== 'all' || sharingFiltersCount(filters) > 0;
  const results = useMemo(
    () => (isCatalogMode ? applySharingFilters(hasQuery ? searchResults : [...products].sort(byName), category, filters) : []),
    [isCatalogMode, hasQuery, searchResults, products, category, filters],
  );
  const priceCeiling = useMemo(() => sharingPriceCeiling(products), [products]);

  const resetAll = () => {
    setFilters(DEFAULT_SHARING_FILTERS);
    setCategory('all');
    setSearchQuery('');
    setSearchFocused(false);
    setResetKey((k) => k + 1);
  };

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const rows = useMemo(() => collections
    .map((c) => ({
      id: c.id,
      title: c.title,
      items: ((c as Collection & { products?: Product[] }).products || [])
        .map((p) => byId.get(p.id))
        .filter((p): p is Product => !!p),
    }))
    .filter((c) => c.items.length > 0), [collections, byId]);

  // Как в каталоге: для бесконечной прокрутки нужно не меньше пяти слайдов
  const loopBanners = useMemo(() => {
    let list = [...banners];
    while (list.length < 5 && banners.length > 1) list = [...list, ...banners];
    return list;
  }, [banners]);

  // Картинки баннеров грузим заранее, чтобы карусель не мигала
  useEffect(() => {
    banners.forEach((b) => { const img = new Image(); img.src = optimizeImage(b.imageUrl, 800); });
  }, [banners]);

  const openBanner = (b: Banner) => {
    if (b.linkType === 'collection' && b.linkUrl) navigate(`/sharing/collection/${b.linkUrl}`);
    else if (b.linkType === 'product' && b.linkUrl) navigate(`/sharing/${b.linkUrl}`);
    // Свои страницы (например «Что такое позиции?») открываем внутри приложения, чужие сайты — снаружи
    else if (b.linkType === 'url' && b.linkUrl) {
      if (b.linkUrl.startsWith('/')) navigate(b.linkUrl);
      else window.open(b.linkUrl, '_blank');
    }
  };
  const subscriptions = products.filter((p) => sharingTypeOf(p) === 'subscription').sort(byName);

  return (
    <Layout>
      <SharingCatalogBar
        initialQuery={searchQuery}
        onQueryChange={handleQueryChange}
        searchFocused={searchFocused}
        onSearchFocus={handleSearchFocus}
        category={category}
        onCategoryChange={setCategory}
        filters={filters}
        onFiltersChange={setFilters}
        priceCeiling={priceCeiling}
        resetKey={resetKey}
      />

      {isCatalogMode ? (
        <div className="px-4 pb-6 pt-3">
          {!loading && results.length > 0 && (
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-text-tertiary">Найдено: {results.length}</p>
              <button onClick={resetAll} className="text-xs text-primary font-medium">Сбросить</button>
            </div>
          )}
          {loading ? (
            <ResultsSkeleton />
          ) : results.length === 0 ? (
            <EmptyState
              title="Ничего не найдено"
              description={hasQuery ? 'Попробуйте изменить параметры поиска или фильтры' : 'В этом разделе пока нет товаров'}
              action={{ label: 'Сбросить фильтры', onClick: resetAll }}
            />
          ) : (
            <SharingResults items={results} grouped={hasQuery} />
          )}
        </div>
      ) : (
      <>
      {/* Баннеры раздела — та же карусель, что в каталоге (4:5, coverflow, автопрокрутка) */}
      {banners.length > 0 && (
        <div className="mb-2 mt-1 banner-carousel">
          <Swiper
            modules={[Autoplay, EffectCoverflow]}
            effect="coverflow"
            coverflowEffect={{ rotate: 0, stretch: 0, depth: 100, modifier: 1, slideShadows: false }}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            loop={banners.length > 1}
            spaceBetween={24}
            slidesPerView={1.5}
            centeredSlides={true}
            grabCursor={true}
            speed={500}
          >
            {loopBanners.map((banner, idx) => (
              <SwiperSlide key={`${banner.id}-${idx}`}>
                <div
                  className="rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform aspect-[4/5] flex items-end relative"
                  onClick={() => openBanner(banner)}
                >
                  <ImageWithLoader
                    src={optimizeImage(banner.imageUrl, 800)}
                    alt={banner.title}
                    wrapperClassName="absolute inset-0 w-full h-full"
                    className="object-cover"
                    spinnerSize={28}
                    loading="eager"
                    decoding="sync"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      <div className="pb-2">
        {loading ? (
          <div className="px-4 pt-3 grid grid-cols-2 gap-x-3 gap-y-5">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-square rounded-[14px] bg-gray-200/70 animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <EmptyState title="Товары скоро появятся" description="Раздел шеринга наполняется" />
        ) : (
          <>
            {rows.map((row) => (
              <section key={row.id} className={CARD}>
                <div className="px-4 flex items-baseline justify-between mb-3">
                  <h2 style={TITLE}>{row.title}</h2>
                  <button onClick={() => navigate(`/sharing/collection/${row.id}`)} style={{ fontSize: 13, fontWeight: 600, color: '#3657F0' }}>Все</button>
                </div>
                <div className="flex overflow-x-auto scrollbar-hide px-4 pb-2" style={{ gap: 12 }}>
                  {row.items.map((p) => <SharingTile key={p.id} product={p} horizontal />)}
                </div>
              </section>
            ))}
            {subscriptions.length > 0 && (
              <section className={CARD}>
                <h2 className="px-4 mb-3" style={TITLE}>Подписки</h2>
                <div className="px-4 grid grid-cols-2 gap-x-3 gap-y-5">
                  {subscriptions.map((p) => <SharingTile key={p.id} product={p} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
      </>
      )}
    </Layout>
  );
};

/** Подборка раздела «Шеринг» — та же панель поиска и фильтров, что в каталоге, и сетка плиток */
export const SharingCollectionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState('Подборка');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SharingCategory>('all');
  const [filters, setFilters] = useState<SharingFilters>(DEFAULT_SHARING_FILTERS);
  const [searchFocused, setSearchFocused] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const setSearchActive = useUIStore((s) => s.setSearchActive);

  useEffect(() => {
    setSearchActive(searchFocused);
    return () => setSearchActive(false);
  }, [searchFocused, setSearchActive]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.getCollection(id, { limit: 200 }).then((c) => {
      if (cancelled || !c) return;
      setTitle(c.title);
      setProducts(c.products.filter((p) => p.category === 'sharing' && cheapestOffer(p)));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const handleSearchFocus = useCallback((focused: boolean) => setSearchFocused(focused), []);

  const searchResults = useProductSearch(products, query);
  const hasQuery = query.trim() !== '';
  const isFiltered = hasQuery || category !== 'all' || sharingFiltersCount(filters) > 0;
  const results = useMemo(
    () => applySharingFilters(hasQuery ? searchResults : products, category, filters),
    [hasQuery, searchResults, products, category, filters],
  );
  const priceCeiling = useMemo(() => sharingPriceCeiling(products), [products]);

  const resetAll = () => {
    setQuery('');
    setCategory('all');
    setFilters(DEFAULT_SHARING_FILTERS);
    setSearchFocused(false);
    setResetKey((k) => k + 1);
  };

  return (
    <Layout>
      <SharingCatalogBar
        initialQuery={query}
        onQueryChange={setQuery}
        searchFocused={searchFocused}
        onSearchFocus={handleSearchFocus}
        category={category}
        onCategoryChange={setCategory}
        filters={filters}
        onFiltersChange={setFilters}
        priceCeiling={priceCeiling}
        resetKey={resetKey}
      />
      <div className="px-4 pb-6">
        <h1 className="pt-3 pb-2" style={PAGE_TITLE}>{title}</h1>
        {!loading && results.length > 0 && (
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-text-tertiary">Найдено: {results.length}</p>
            {isFiltered && <button onClick={resetAll} className="text-xs text-primary font-medium">Сбросить</button>}
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-5">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-square rounded-[14px] bg-gray-200/70 animate-pulse" />)}
          </div>
        ) : results.length === 0 ? (
          isFiltered ? (
            <EmptyState
              title="Ничего не найдено"
              description="Попробуйте изменить параметры поиска или фильтры"
              action={{ label: 'Сбросить фильтры', onClick: resetAll }}
            />
          ) : (
            <EmptyState title="В подборке пока пусто" description="Загляните позже" />
          )
        ) : (
          <SharingResults items={results} grouped={hasQuery} />
        )}
      </div>
    </Layout>
  );
};
